/**
 * Range bar — the navigation strip above the chart group.
 *
 * Mirrors the System Status timeline's range bar (DeviceStateTimeline.tsx
 * lines ~895–1011) so operators who learned one chart's navigation
 * vocabulary get the other for free:
 *   - range chips: 1h / 6h / 24h / 7d / 30d / 90d
 *   - ◀▶ slide controls — pan the window by half-span steps
 *   - 📅 jump-to-date — native datetime-local overlaid invisibly on a chip
 *   - live indicator — dim "live" pill when tracking now; click-to-snap
 *     "X ago · now" when paged back
 *   - Reset chip — visible only when in brush-zoom (custom span), snaps
 *     back to the nearest preset range
 *
 * Sample count + range label live in the page's status line above this
 * bar (so this component only owns navigation, not data summary).
 */
import { useRef } from 'react';
import {
  TELEMETRY_RANGES,
  type TelemetryRangeKey,
} from './useTelemetryUrlState';

interface TelemetryRangeBarProps {
  rangeKey: TelemetryRangeKey | 'custom';
  isLive: boolean;
  untilMs: number;
  nowMs: number;
  /** Half the current span — drives ◀▶ step size + Jump-to anchor. */
  halfSpanMs: number;
  isCustomSpan: boolean;
  setRangeKey: (key: TelemetryRangeKey) => void;
  setCentreMs: (next: number | null | ((c: number | null) => number | null)) => void;
  resetCustom: () => void;
}

// Browser <input type="datetime-local"> wants `YYYY-MM-DDTHH:mm` in local
// time (no Z, no seconds). toISOString() would give UTC, so format manually.
function toLocalDatetimeInputValue(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fmtOffsetAgo(deltaMs: number): string {
  const abs = Math.abs(deltaMs);
  if (abs < 60_000) return `${Math.round(abs / 1000)} s`;
  if (abs < 3_600_000) return `${Math.round(abs / 60_000)} min`;
  if (abs < 86_400_000) return `${Math.round(abs / 3_600_000)} h`;
  return `${Math.round(abs / 86_400_000)} d`;
}

export function TelemetryRangeBar({
  rangeKey,
  isLive,
  untilMs,
  nowMs,
  halfSpanMs,
  isCustomSpan,
  setRangeKey,
  setCentreMs,
  resetCustom,
}: TelemetryRangeBarProps) {
  const jumpInputRef = useRef<HTMLInputElement | null>(null);
  // Pan step = max(halfSpan, 1 minute). The 1-minute floor protects the
  // shortest 1h range from sub-pixel pan increments.
  const stepMs = Math.max(halfSpanMs, 60_000);

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {/* Slide controls — page the window backwards / forwards by half-
          window steps. `live` indicator doubles as the snap-back button
          when paged back. ▶ disabled at the live edge. */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() =>
            setCentreMs((c) => (c === null ? nowMs - halfSpanMs : c) - stepMs)
          }
          className="px-2 py-1 rounded-md text-xs font-medium bg-soft text-muted hover:text-fg"
          title={`Step back ${Math.round(stepMs / 60_000)} min`}
          aria-label="Earlier"
        >
          ◀
        </button>
        {isLive ? (
          <span
            className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted tabular-nums"
            title="Window ends at now"
          >
            live
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setCentreMs(null)}
            className="px-2 py-1 rounded-md text-[10px] uppercase tracking-wide text-accent hover:underline tabular-nums"
            title="Snap window back to now"
          >
            {fmtOffsetAgo(nowMs - untilMs)} ago · now
          </button>
        )}
        <button
          type="button"
          onClick={() =>
            setCentreMs((c) => {
              if (c === null) return null;
              const next = c + stepMs;
              return next + halfSpanMs >= nowMs ? null : next;
            })
          }
          disabled={isLive}
          className="px-2 py-1 rounded-md text-xs font-medium bg-soft text-muted hover:text-fg disabled:opacity-30 disabled:cursor-not-allowed"
          title={`Step forward ${Math.round(stepMs / 60_000)} min`}
          aria-label="Later"
        >
          ▶
        </button>
        {/* Jump-to datetime picker — same trick as the state timeline:
            invisible native <input type="datetime-local"> overlaid on
            top of the 📅 chip. Clicking the chip hits the input, the
            browser opens its own picker, and the input's bounding box
            anchors the picker correctly (sr-only would clip it
            offscreen in some browsers and break close-on-outside-click). */}
        <span
          className="relative inline-block"
          title="Jump to a specific date and time"
        >
          <button
            type="button"
            tabIndex={-1}
            aria-hidden="true"
            className="px-2 py-1 rounded-md text-xs font-medium bg-soft text-muted hover:text-fg pointer-events-none"
          >
            📅
          </button>
          <input
            ref={jumpInputRef}
            type="datetime-local"
            step={60}
            max={toLocalDatetimeInputValue(nowMs)}
            value={toLocalDatetimeInputValue(untilMs)}
            aria-label="Jump to date and time"
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            onChange={(e) => {
              const v = e.target.value;
              if (!v) return;
              const t = new Date(v).getTime();
              if (!Number.isFinite(t)) return;
              const clamped = Math.min(t, nowMs);
              // Within 60 s of now → treat as "back to live". Avoids a
              // confusing UX where picking "now" parks the window
              // immediately off-live.
              if (clamped >= nowMs - 60_000) {
                setCentreMs(null);
              } else {
                setCentreMs(clamped - halfSpanMs);
              }
            }}
          />
        </span>
      </div>
      <div className="flex gap-1 items-center">
        {isCustomSpan && (
          <button
            type="button"
            onClick={resetCustom}
            className="px-2 py-1 rounded-md text-xs font-medium bg-warn-soft text-warn hover:bg-warn hover:text-white"
            title="Exit brush-zoom — snap to nearest preset range"
          >
            ↺ Reset
          </button>
        )}
        {TELEMETRY_RANGES.map((r) => (
          <button
            key={r.key}
            onClick={() => setRangeKey(r.key)}
            className={`px-2.5 py-1 rounded-md text-xs font-medium ${
              rangeKey === r.key
                ? 'bg-accent text-white'
                : 'bg-soft text-muted hover:text-fg'
            }`}
            title={r.label}
          >
            {r.key}
          </button>
        ))}
      </div>
    </div>
  );
}
